// Client-side mirror of the buyer fee in server/services/feeService.js. The
// server is always authoritative — orders snapshot their own percentages — but
// checkout, offer, and listing screens need the same number to quote a total
// before the order exists. Keep it in step with feeService.js.
//
// The seller fee is mirrored for one screen only: the order page's draft
// agreement quotes the freelancer their payout before the order is priced.
export const BUYER_FEE_PCT = 10; // added on top of the price at checkout
export const SELLER_FEE_PCT = 5; // taken out of the freelancer's payout

// What the freelancer receives for a given price, commission taken out.
// Postage passes through whole, same as computeFees on the server.
export function sellerPayout(price, sellerFeePct = SELLER_FEE_PCT, shipping = 0) {
  const priceCents = Math.round(Number(price) * 100);
  const shipCents = Math.max(0, Math.round(Number(shipping || 0) * 100));
  return (priceCents - Math.round((priceCents * sellerFeePct) / 100) + shipCents) / 100;
}

// Card processing passed to the client: Stripe's 2.9% + $0.30, grossed up so
// it also covers itself. Same formula as computeFees on the server, which is
// authoritative; this only quotes it before the order exists.
function withProcessingCents(baseCents) {
  return baseCents > 0 ? Math.ceil(((baseCents + 30) * 1000) / 971) : 0;
}

function baseCents(price, buyerFeePct, shipping) {
  const priceCents = Math.round(Number(price) * 100);
  const shipCents = Math.max(0, Math.round(Number(shipping || 0) * 100));
  return priceCents + Math.round((priceCents * buyerFeePct) / 100) + shipCents;
}

// What a client pays for a given list price, fee and processing included.
// Postage rides on the same charge but outside the fee, same as computeFees
// on the server. `processing` false is for orders booked before the line
// existed (their processingFee is 0).
export function buyerTotal(price, buyerFeePct = BUYER_FEE_PCT, shipping = 0, processing = true) {
  const base = baseCents(price, buyerFeePct, shipping);
  return (processing ? withProcessingCents(base) : base) / 100;
}

// What a stored order, contract or invoice costs paid by bank: its total
// without the processing line (server/services/feeService.js bankAmount).
export function bankTotal(doc) {
  return (Math.round(Number(doc?.totalCharged || 0) * 100) - Math.round(Number(doc?.processingFee || 0) * 100)) / 100;
}

// The processing line alone, for line-item display.
export function processingFee(price, buyerFeePct = BUYER_FEE_PCT, shipping = 0) {
  const base = baseCents(price, buyerFeePct, shipping);
  return (withProcessingCents(base) - base) / 100;
}

// The client's fee alone, for line-item display.
export function buyerFee(price, buyerFeePct = BUYER_FEE_PCT) {
  const priceCents = Math.round(Number(price) * 100);
  return Math.round((priceCents * buyerFeePct) / 100) / 100;
}
